/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Form, Space } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreateApiTokenResponse, CreateToken } from '@/client';
import { ContentContainer } from '@/components/ContentContainer';
import { validationErrors } from '@/components/Forms/validationErrors';
import { PageHeader } from '@/components/PageHeader';
import { useMessage } from '@/hooks/message';
import { useNewAPITokenMutation } from '@/hooks/mutation/useNewAPITokenMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { APITokenCreatedModal } from '@/pages/APITokens/APITokenCreatedModal';
import { PermissionsSection } from '@/pages/APITokens/PermissionsSection';
import { TokenFormActions } from '@/pages/APITokens/TokenFormActions';
import { TokenNameSection } from '@/pages/APITokens/TokenNameSection';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { useAvailablePermissions } from './hooks/useAvailablePermissions';

type PermissionState = {
  [permissionName: string]: boolean;
};

export function CreateAPITokenPage() {
  const { t } = useMimicTranslation('apiTokens');
  const [form] = Form.useForm<CreateToken>();
  const navigate = useNavigate();
  const [message] = useMessage();
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [permissionState, setPermissionState] = useState<PermissionState>({});
  const [createdToken, setCreatedToken] = useState<CreateApiTokenResponse | undefined>();

  const { data: permissionGroups, isLoading: permissionsLoading } = useAvailablePermissions();

  const { mutate: createAPIToken, isPending } = useNewAPITokenMutation({
    onError: (error) => {
      message.error(t('creationError'));
      if (error instanceof Error) {
        // Handle validation errors
        form.setFields(validationErrors<CreateToken>({ name: error.message, permissions: '' }));
      }
    },
    onSuccess: (apiToken) => {
      message.success(t('apiTokenCreated'));
      setCreatedToken(apiToken);
    },
  });

  const selectedPermissions = Object.entries(permissionState)
    .filter(([, isEnabled]) => isEnabled)
    .map(([permission]) => permission);

  const onFinish = (values: CreateToken) => {
    createAPIToken({
      name: values.name.trim(),
      permissions: selectedPermissions,
    });
  };

  const handlePermissionToggle = (permissionName: string, enabled: boolean) => {
    setPermissionState((prev) => ({
      ...prev,
      [permissionName]: enabled,
    }));
  };

  const handleModalClose = () => {
    setCreatedToken(undefined);
    navigate(`/tenants/${tenantID}/api-tokens`);
  };

  if (!tenantID) return null;

  return (
    <ContentContainer width="100%">
      <Container gap="lg" style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
        <Container gap="sm">
          <PageHeader title={t('createTokenModal.title')} level={2} />
          <Text type="subtle" style={{ fontSize: '15px', lineHeight: '1.4' }}>
            {t('description')}
          </Text>
        </Container>

        <Form form={form} layout="vertical" onFinish={onFinish} style={{ width: '100%' }} disabled={isPending}>
          <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <TokenNameSection />

            <PermissionsSection
              permissionGroups={permissionGroups}
              permissionsLoading={permissionsLoading}
              permissionState={permissionState}
              onPermissionToggle={handlePermissionToggle}
            />

            <TokenFormActions
              onCancel={() => navigate(`/tenants/${tenantID}/api-tokens`)}
              isSubmitting={isPending}
              hasSelectedPermissions={selectedPermissions.length > 0}
            />
          </Space>
        </Form>
      </Container>
      {createdToken && <APITokenCreatedModal createdToken={createdToken} onClose={handleModalClose} />}
    </ContentContainer>
  );
}
